import { useId, useMemo } from 'react';

import { useOrbitTilt } from './useOrbitTilt.js';
import styles from './SceneAtmosphere.module.css';

/**
 * The atmosphere behind the orbital scene: a scattered starfield, a core
 * glow, twelve pulsing sweep spokes that read together as one radar sweep,
 * and three expanding radar pings. Lifted from `design/Main.dc.html`.
 *
 * This component renders SVG elements only — `<defs>`, a starfield `<g>`, a
 * glow `<ellipse>`, a spokes `<g>`, a pings `<g>`, and a grain `<rect>` — and
 * is meant to be spread directly inside the caller's own `<svg>`:
 *
 *   <svg viewBox={`0 0 ${w} ${h}`}>
 *     <SceneAtmosphere viewBoxWidth={w} viewBoxHeight={h} centerX={cx} centerY={cy} />
 *     ...the rest of the scene, drawn on top...
 *   </svg>
 *
 * It never renders its own `<svg>` wrapper. The exception is the scanlines
 * and grain, exported together as a CSS class (`projectionOverlayClassName`)
 * for the caller to apply to a full-bleed `<div>` of its own — see the
 * comment on `.projection` in SceneAtmosphere.module.css for why those two
 * stay outside the SVG tree.
 *
 * Only translation, scale, and opacity animate here. A rigid rotation
 * preserves a circle but shears an ellipse off its own path — an early draft
 * of this app spun a group of orbits and threw two nodes into empty space.
 * That is exactly why the sweep is twelve pulsing spokes rather than one
 * rotating line, and why the pings animate by scale rather than a spin.
 */

export type SceneAtmosphereProps = {
  /**
   * Width/height of the caller's own `<svg viewBox="0 0 W H">`. Every
   * position generated here is in those same units, so this component works
   * in a scene that is not 1440x900 (the design's own canvas size).
   */
  viewBoxWidth: number;
  viewBoxHeight: number;
  /** Centre the core glow, sweep spokes, and radar pings all share. */
  centerX: number;
  centerY: number;
  /**
   * Inner/outer radius the twelve sweep spokes span, in viewBox units.
   * Defaults keep the design's own inner:outer:height ratio (100:430:900).
   */
  spokeInnerRadius?: number;
  spokeOuterRadius?: number;
  /** How many spokes make up the sweep. The design uses twelve. */
  spokeCount?: number;
  /**
   * Semi-major (x) radius of the core glow ellipse. The y radius is always
   * this times `--orbit-tilt`, read from web/src/design/tokens.css, never a
   * copy of that ratio written here — see the module doc comment below.
   */
  glowRadiusX?: number;
  /** Semi-major (x) radius of each radar ping ellipse; y radius likewise `* tilt`. */
  pingRadiusX?: number;
  /** How many stars to scatter across the viewBox. */
  starCount?: number;
  /**
   * Seed for the starfield's pseudo-random scatter. Two instances that want
   * visibly different (but each individually stable) skies should pass
   * different seeds.
   */
  starSeed?: number;
  /** Opacity of the full-bleed grain rect. */
  grainOpacity?: number;
  /**
   * Forces the perspective ratio instead of reading `--orbit-tilt`. The
   * stage passes the value it resolved once, so every layer of one scene is
   * guaranteed to be drawn at an identical tilt.
   */
  tilt?: number;
};

const DEFAULT_SPOKE_COUNT = 12;
const DEFAULT_STAR_COUNT = 22;
const DEFAULT_STAR_SEED = 1;
const DEFAULT_GRAIN_OPACITY = 0.035;

// Ratios lifted from the design's own 1440x900 canvas, so the defaults look
// right at other sizes too: inner/outer spoke radius scale with the box's
// height, glow/ping radius scale with its width.
const DEFAULT_SPOKE_INNER_RATIO = 100 / 900;
const DEFAULT_SPOKE_OUTER_RATIO = 430 / 900;
const DEFAULT_GLOW_RADIUS_X_RATIO = 300 / 1440;
const DEFAULT_PING_RADIUS_X_RATIO = 96 / 1440;

const SWEEP_CYCLE_SECONDS = 6;

/*
 * The tilt is read through `useOrbitTilt` (see useOrbitTilt.ts), the one
 * place in the app that reads `--orbit-tilt` off the document — so the
 * atmosphere, the orbit rings and the body scan can never disagree about
 * what angle this plane is seen at.
 */

type Star = { x: number; y: number; r: number; opacity: number };

/**
 * A tiny seeded PRNG (mulberry32), so the starfield is deterministic from
 * (width, height, count, seed) rather than `Math.random()`. Called only
 * inside the `useMemo` below, never during render itself, so re-rendering
 * the component (a state update elsewhere in the scene, a prop that isn't
 * one of the memo's dependencies) never moves a single star.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateStars(width: number, height: number, count: number, seed: number): Star[] {
  const rand = mulberry32(seed);
  return Array.from({ length: count }, () => ({
    x: rand() * width,
    y: rand() * height,
    r: 1 + rand() * 0.5, // 1 to 1.5, matching the design's scatter
    opacity: 0.22 + rand() * 0.28, // .22 to .50
  }));
}

export type SweepSpoke = { x1: number; y1: number; x2: number; y2: number; delaySeconds: number };

/**
 * Generates the sweep spokes rather than pasting the design's hardcoded
 * endpoints, which only ever worked at its one centre and one radius.
 *
 * A spoke at angle theta ends at (cx + r*cos(theta), cy + r*sin(theta)*tilt)
 * so it lies ON the tilted plane — the same reasoning as every ellipse in
 * this app, just applied to a radial line instead of a ring.
 */
export function generateSweepSpokes(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  tilt: number,
  count: number = DEFAULT_SPOKE_COUNT,
): SweepSpoke[] {
  const angleStep = (Math.PI * 2) / count;
  const delayStep = SWEEP_CYCLE_SECONDS / count;
  return Array.from({ length: count }, (_, k) => {
    const theta = k * angleStep;
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);
    return {
      x1: cx + innerRadius * cosT,
      y1: cy + innerRadius * sinT * tilt,
      x2: cx + outerRadius * cosT,
      y2: cy + outerRadius * sinT * tilt,
      delaySeconds: -(k * delayStep),
    };
  });
}

/** CSS class for the scanlines-and-grain overlay — see the doc comment on
 * `.projection` in SceneAtmosphere.module.css for why these two textures are
 * exported for the caller to place rather than rendered inside the scene.
 * Apply it to a full-bleed `<div>` inside a `position: relative` container,
 * e.g. the page itself. */
export const projectionOverlayClassName = styles.projection;

export function SceneAtmosphere({
  viewBoxWidth,
  viewBoxHeight,
  centerX,
  centerY,
  spokeInnerRadius,
  spokeOuterRadius,
  spokeCount = DEFAULT_SPOKE_COUNT,
  glowRadiusX,
  pingRadiusX,
  starCount = DEFAULT_STAR_COUNT,
  starSeed = DEFAULT_STAR_SEED,
  grainOpacity = DEFAULT_GRAIN_OPACITY,
  tilt: tiltOverride,
}: SceneAtmosphereProps) {
  const coreGlowId = useId();
  const grainId = useId();
  const tilt = useOrbitTilt(tiltOverride);

  const resolvedSpokeInner = spokeInnerRadius ?? viewBoxHeight * DEFAULT_SPOKE_INNER_RATIO;
  const resolvedSpokeOuter = spokeOuterRadius ?? viewBoxHeight * DEFAULT_SPOKE_OUTER_RATIO;
  const resolvedGlowRadiusX = glowRadiusX ?? viewBoxWidth * DEFAULT_GLOW_RADIUS_X_RATIO;
  const resolvedPingRadiusX = pingRadiusX ?? viewBoxWidth * DEFAULT_PING_RADIUS_X_RATIO;

  const stars = useMemo(
    () => generateStars(viewBoxWidth, viewBoxHeight, starCount, starSeed),
    [viewBoxWidth, viewBoxHeight, starCount, starSeed],
  );

  const spokes = useMemo(
    () => generateSweepSpokes(centerX, centerY, resolvedSpokeInner, resolvedSpokeOuter, tilt, spokeCount),
    [centerX, centerY, resolvedSpokeInner, resolvedSpokeOuter, tilt, spokeCount],
  );

  const glowRadiusY = resolvedGlowRadiusX * tilt;
  const pingRadiusY = resolvedPingRadiusX * tilt;
  const pingTransformOrigin = `${centerX}px ${centerY}px`;

  return (
    <>
      <defs>
        <radialGradient id={coreGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
          <stop offset="45%" stopColor="var(--accent)" stopOpacity="0.10" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
        <filter id={grainId}>
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves={3} />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>

      <g aria-hidden="true" fill="var(--text)">
        {stars.map((star, index) => (
          <circle key={index} cx={star.x} cy={star.y} r={star.r} opacity={star.opacity} />
        ))}
      </g>

      <ellipse
        aria-hidden="true"
        cx={centerX}
        cy={centerY}
        rx={resolvedGlowRadiusX}
        ry={glowRadiusY}
        fill={`url(#${coreGlowId})`}
      />

      {/* In-plane spokes. Their staggered pulse is the sweep — see the
          module CSS doc comment on why this is opacity, never a rotation. */}
      <g aria-hidden="true" fill="none" stroke="var(--accent)" strokeWidth="1" opacity=".07">
        {spokes.map((spoke, index) => (
          <path
            key={index}
            className={styles.spoke}
            d={`M ${spoke.x1} ${spoke.y1} L ${spoke.x2} ${spoke.y2}`}
            style={{ animationDelay: `${spoke.delaySeconds}s` }}
          />
        ))}
      </g>

      {/* Radar pings, expanding across the plane by scale — never rotated. */}
      <g aria-hidden="true" fill="none" stroke="var(--accent-bright)" strokeWidth="1.5">
        <ellipse
          className={styles.ping}
          cx={centerX}
          cy={centerY}
          rx={resolvedPingRadiusX}
          ry={pingRadiusY}
          style={{ transformOrigin: pingTransformOrigin }}
        />
        <ellipse
          className={`${styles.ping} ${styles.ping2}`}
          cx={centerX}
          cy={centerY}
          rx={resolvedPingRadiusX}
          ry={pingRadiusY}
          style={{ transformOrigin: pingTransformOrigin }}
        />
        <ellipse
          className={`${styles.ping} ${styles.ping3}`}
          cx={centerX}
          cy={centerY}
          rx={resolvedPingRadiusX}
          ry={pingRadiusY}
          style={{ transformOrigin: pingTransformOrigin }}
        />
      </g>

      <rect
        aria-hidden="true"
        x={0}
        y={0}
        width={viewBoxWidth}
        height={viewBoxHeight}
        filter={`url(#${grainId})`}
        opacity={grainOpacity}
      />
    </>
  );
}
